import React, { useEffect, useMemo, useState } from 'react'
import { Order, OrderItemTrackingInfo } from '@src/types/graphql-types'
import { CircularProgress } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { messages } from '../messages'
import { messages as commonMessages } from '@src/utils/messages'
import GlobalAlert from '@src/components/GlobalAlert'
import LinkStyleButton from '@src/components/LinkStyleButton/LinkStyleButton'
import { ShipmentSummaryModal } from './ShipmentSummaryModal'
import { makeStyles } from '@material-ui/core/styles'
import CaretRightIcon from '@src/icons/CaretRightIcon'
import { groupOrderItems, OrderItemFragmentWithMergedLines } from './utils'
import { sendOrderCenterEvent } from '@src/routes/OrderCenter/analytics'
import { useOrderExpansionQuery } from './OrderRowExpansion'

const MAX_ITEMS_TRUNCATED = 5

const useStyles = makeStyles((theme) => ({
  list: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    '& > * + *': {
      marginTop: theme.spacing(4),
    },
  },
  orderItemButton: {
    background: theme.palette.common.white,
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey[400]}`,
    font: 'inherit',
    lineHeight: 1,
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(4),
    cursor: 'pointer',
  },
  caretIcon: {
    fontSize: theme.typography.pxToRem(10),
  },
  seeAll: {
    textAlign: 'center',
    marginTop: theme.spacing(6),
  },
  loadingIndicator: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    color: theme.palette.grey[700],
    textTransform: 'uppercase',
    '& > * + *': {
      marginLeft: theme.spacing(2),
    },
  },
}))

interface MobileOrderItemListProps {
  order: Order
  setTrackingInfo: React.Dispatch<
    React.SetStateAction<OrderItemTrackingInfo | null>
  >
  skipQuery?: boolean
  userId: string
}

export const MobileOrderItemList: React.FC<MobileOrderItemListProps> = ({
  order,
  setTrackingInfo,
  skipQuery,
  userId,
}) => {
  const classes = useStyles()
  const { loading, data, error } = useOrderExpansionQuery(order, {
    skip: skipQuery,
  })
  const [truncationEnforced, setTruncationEnforced] = useState(true)
  const [shipmentSummaryItem, setShipmentSummaryItem] =
    useState<OrderItemFragmentWithMergedLines | null>(null)

  useEffect(() => {
    if (!skipQuery) setTruncationEnforced(true)
  }, [skipQuery])

  const orderDetails = data?.orderDetail?.order
  const items = data?.orderDetail.order.items
  const groupedItems = useMemo(
    () => groupOrderItems(items ? items : []),
    [items]
  )

  if (loading) {
    return (
      <div className={classes.loadingIndicator}>
        <CircularProgress size={20} thickness={5} />
        <span>
          <FormattedMessage {...messages.LOADING} />
          ...
        </span>
      </div>
    )
  }

  if (error || !items?.length) {
    return (
      <GlobalAlert severity="error">
        <FormattedMessage {...messages.ORDER_DETAILS_UNAVAILABLE} />
      </GlobalAlert>
    )
  }

  const truncated =
    truncationEnforced && groupedItems.length > MAX_ITEMS_TRUNCATED
  const visibleItems = truncated
    ? groupedItems.slice(0, MAX_ITEMS_TRUNCATED)
    : groupedItems

  return (
    <>
      <ul className={classes.list}>
        {visibleItems.map((item, i) => (
          <li key={i}>
            <button
              className={classes.orderItemButton}
              onClick={() => setShipmentSummaryItem(item)}
            >
              {item.materialNumber}
              <span>
                <CaretRightIcon className={classes.caretIcon} />
              </span>
            </button>
          </li>
        ))}
      </ul>
      {truncated && (
        <div className={classes.seeAll}>
          <LinkStyleButton
            onClick={() => {
              setTruncationEnforced(false)
              sendOrderCenterEvent('see all order items', userId)
            }}
          >
            <FormattedMessage {...commonMessages.SEE_ALL} />
            {` (${groupedItems.length})`}
          </LinkStyleButton>
        </div>
      )}
      {shipmentSummaryItem && orderDetails && (
        <ShipmentSummaryModal
          order={orderDetails}
          item={shipmentSummaryItem}
          setTrackingInfo={setTrackingInfo}
          onClose={() => setShipmentSummaryItem(null)}
        />
      )}
    </>
  )
}
